import React from 'react';

export interface NotificationFeatures {
  /**
   * Whether we should display selection boxes on notifications
   */
  selection: boolean;

  /**
   * Whether we should allow marking all messages of a type as read
   */
  markAllOfTypeAsSeen: boolean;

  /**
   * Whether we should allow move operations on messages. This includes:
   * - Deleting
   * - Saving
   */
  moveMessages: boolean;
}

const defaultFeatures: NotificationFeatures = {
  selection: false,
  markAllOfTypeAsSeen: false,
  moveMessages: false,
};

/**
 * Notifications have a variety of actions that can be performed, but all actions don't make sense in all contexts. For instance,
 * we don't always want the user to be able to select multiple notifications. This context contains flags that will turn
 * features (like selection) off or on.
 *
 * ---
 *
 * **Note:** flipping a flag here doesn't mean everything is in place for a feature to work. For example, it's up to the consumer
 * to implement an actionbar for interacting with selected messages. Toggling the {@link NotificationFeatures.selection} feature will only
 * turn off selection boxes inside individual notification components.
 */
export const NotificationFeaturesContext =
  React.createContext<NotificationFeatures>(defaultFeatures);
NotificationFeaturesContext.displayName = 'NotificationFeaturesContext';

export const NotificationFeaturesContextProvider: React.FC<{
  features: NotificationFeatures;
  children: React.ReactNode;
}> = ({ children, features }) => {
  return (
    <NotificationFeaturesContext.Provider value={features}>
      {children}
    </NotificationFeaturesContext.Provider>
  );
};
